import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import api from "../lib/api";

const empty = { author: "", role: "", quote: "", rating: 5, published: true };

const TabButton = ({ id, active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`font-mono-ui text-[11px] tracking-[0.25em] uppercase ${active ? "text-gold" : "text-ivory/60"}`}
    data-testid={`tab-${id}`}
  >
    {children}
  </button>
);

const ReviewForm = ({ form, setForm, editingId, onSave, onCancel }) => (
  <section className="col-span-12 md:col-span-5">
    <div className="font-mono-ui text-[10px] tracking-[0.3em] text-gold uppercase mb-3">
      {editingId ? "Edit Review" : "New Review"}
    </div>
    <form onSubmit={onSave} className="space-y-6 border border-ivory/10 p-8" data-testid="review-form">
      <div>
        <label className="block font-mono-ui text-[10px] tracking-[0.3em] uppercase text-ivory/60 mb-2">Author</label>
        <input required className="field-line" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} data-testid="review-author" />
      </div>
      <div>
        <label className="block font-mono-ui text-[10px] tracking-[0.3em] uppercase text-ivory/60 mb-2">Role / Location</label>
        <input className="field-line" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="review-role" />
      </div>
      <div>
        <label className="block font-mono-ui text-[10px] tracking-[0.3em] uppercase text-ivory/60 mb-2">Quote</label>
        <textarea required rows={4} className="field-line resize-none" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} data-testid="review-quote" />
      </div>
      <div className="flex items-center gap-6">
        <div>
          <label className="block font-mono-ui text-[10px] tracking-[0.3em] uppercase text-ivory/60 mb-2">Rating</label>
          <select className="bg-transparent border border-ivory/20 px-3 py-2 text-ivory" value={form.rating} onChange={(e) => setForm({ ...form, rating: parseInt(e.target.value) })} data-testid="review-rating">
            {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n} className="bg-navy">{n}★</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-ivory/70 cursor-pointer mt-6">
          <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="review-published" /> Published
        </label>
      </div>
      <div className="flex gap-3">
        <button type="submit" className="px-6 py-3 bg-gold text-navy font-mono-ui text-[11px] tracking-[0.25em] uppercase" data-testid="review-save">
          {editingId ? "Update" : "Publish"} →
        </button>
        {editingId && (
          <button type="button" onClick={onCancel} className="px-6 py-3 border border-ivory/20 font-mono-ui text-[11px] tracking-[0.25em] uppercase text-ivory/70">
            Cancel
          </button>
        )}
      </div>
    </form>
  </section>
);

const ReviewList = ({ reviews, onEdit, onDelete }) => (
  <section className="col-span-12 md:col-span-7">
    <div className="font-mono-ui text-[10px] tracking-[0.3em] text-gold uppercase mb-3">All Reviews</div>
    <div className="space-y-3">
      {reviews.map((r) => (
        <div key={r.id} className="border border-ivory/10 p-6" data-testid={`admin-review-${r.id}`}>
          <div className="flex justify-between items-start gap-4">
            <div>
              <div className="font-display text-xl">{r.author}</div>
              <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-ivory/50">{r.role} · {r.rating}★ · {r.published ? "Live" : "Draft"}</div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => onEdit(r)} className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-gold hover:text-ivory">Edit</button>
              <button onClick={() => onDelete(r.id)} className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-ivory/60 hover:text-red-400" data-testid={`review-delete-${r.id}`}>Delete</button>
            </div>
          </div>
          <p className="mt-4 text-ivory/80 italic">&ldquo;{r.quote}&rdquo;</p>
        </div>
      ))}
      {reviews.length === 0 && <div className="text-ivory/50 italic">No reviews yet.</div>}
    </div>
  </section>
);

const BookingsList = ({ bookings }) => (
  <section>
    <div className="font-mono-ui text-[10px] tracking-[0.3em] text-gold uppercase mb-3">Booking Enquiries</div>
    <div className="space-y-3">
      {bookings.map((b) => (
        <div key={b.id} className="border border-ivory/10 p-6" data-testid={`admin-booking-${b.id}`}>
          <div className="flex justify-between items-start">
            <div>
              <div className="font-display text-xl">{b.name}</div>
              <div className="font-mono-ui text-[11px] tracking-[0.2em] text-gold">{b.email}</div>
            </div>
            <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-ivory/50">
              {new Date(b.created_at).toLocaleString()}
            </div>
          </div>
          {b.message && <p className="mt-4 text-ivory/70 whitespace-pre-wrap">{b.message}</p>}
        </div>
      ))}
      {bookings.length === 0 && <div className="text-ivory/50 italic">No enquiries yet.</div>}
    </div>
  </section>
);

const PaymentsList = ({ payments }) => (
  <section>
    <div className="font-mono-ui text-[10px] tracking-[0.3em] text-gold uppercase mb-3">Consultation Payments</div>
    <div className="space-y-3">
      {payments.map((p) => (
        <div key={p.paypal_order_id} className="border border-ivory/10 p-6" data-testid={`admin-payment-${p.paypal_order_id}`}>
          <div className="flex justify-between items-start flex-wrap gap-3">
            <div>
              <div className="font-display text-xl">{p.payer_name || "—"}</div>
              <div className="font-mono-ui text-[11px] tracking-[0.2em] text-gold">{p.payer_email || "—"}</div>
            </div>
            <div className="text-right">
              <div className="font-display text-2xl text-gold">{p.currency} {p.amount}</div>
              <div className={`font-mono-ui text-[10px] tracking-[0.25em] uppercase ${p.status === "COMPLETED" ? "text-gold" : "text-ivory/50"}`}>
                {p.status}
              </div>
            </div>
          </div>
          <div className="mt-4 font-mono-ui text-[10px] tracking-[0.2em] uppercase text-ivory/40 flex flex-wrap gap-4">
            <span>Order: {p.paypal_order_id}</span>
            {p.capture_id && <span>Capture: {p.capture_id}</span>}
            <span>{new Date(p.created_at).toLocaleString()}</span>
          </div>
          {p.notes && <p className="mt-3 text-ivory/70 text-sm whitespace-pre-wrap">{p.notes}</p>}
        </div>
      ))}
      {payments.length === 0 && <div className="text-ivory/50 italic">No payments yet.</div>}
    </div>
  </section>
);

export default function AdminDashboard() {
  const [reviews, setReviews] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tab, setTab] = useState("reviews");
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [r, b, p] = await Promise.all([
        api.get("/admin/reviews"),
        api.get("/admin/bookings"),
        api.get("/admin/payments"),
      ]);
      setReviews(r.data);
      setBookings(b.data);
      setPayments(p.data);
    } catch {
      navigate("/admin/login");
    }
  }, [navigate]);

  useEffect(() => {
    if (!sessionStorage.getItem("atelier_token")) navigate("/admin/login");
    else load();
  }, [load, navigate]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/admin/reviews/${editingId}`, form);
        toast.success("Review updated.");
      } else {
        await api.post("/admin/reviews", form);
        toast.success("Review published.");
      }
      setForm(empty);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed.");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    await api.delete(`/admin/reviews/${id}`);
    toast.success("Deleted.");
    load();
  };

  const edit = (r) => {
    setEditingId(r.id);
    setForm({ author: r.author, role: r.role || "", quote: r.quote, rating: r.rating, published: r.published });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(empty);
  };

  const logout = () => {
    sessionStorage.removeItem("atelier_token");
    sessionStorage.removeItem("atelier_user");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-navy text-ivory" data-testid="admin-dashboard">
      <div className="grain" />
      <Toaster theme="dark" position="bottom-right" />
      <header className="px-6 md:px-10 py-6 border-b border-ivory/10 flex justify-between items-center">
        <Link to="/" className="font-display text-xl">The Visa <span className="italic text-gold">Atelier</span> · Admin</Link>
        <div className="flex gap-6 items-center">
          <TabButton id="reviews" active={tab === "reviews"} onClick={() => setTab("reviews")}>Reviews ({reviews.length})</TabButton>
          <TabButton id="bookings" active={tab === "bookings"} onClick={() => setTab("bookings")}>Bookings ({bookings.length})</TabButton>
          <TabButton id="payments" active={tab === "payments"} onClick={() => setTab("payments")}>Payments ({payments.length})</TabButton>
          <button onClick={logout} className="font-mono-ui text-[11px] tracking-[0.25em] uppercase text-ivory/60 hover:text-gold" data-testid="admin-logout">Logout</button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        {tab === "reviews" && (
          <div className="grid grid-cols-12 gap-8">
            <ReviewForm form={form} setForm={setForm} editingId={editingId} onSave={save} onCancel={cancelEdit} />
            <ReviewList reviews={reviews} onEdit={edit} onDelete={del} />
          </div>
        )}
        {tab === "bookings" && <BookingsList bookings={bookings} />}
        {tab === "payments" && <PaymentsList payments={payments} />}
      </main>
    </div>
  );
}
